import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { followUpApi } from '../lib/api'
import type {
  AssignCallBatchRequest,
  CallBatchesParams,
  CreateCallBatchRequest,
  SaveFollowUpScriptRequest,
} from '../types/followUp'

// CF-1 P4 organizer call-list hooks. The canvasser (grant) side lives in Task 9's own hooks.

export function useCallBatches(campaignId: string | undefined, params: CallBatchesParams = {}) {
  return useQuery({
    queryKey: ['followUp', campaignId, 'callBatches', params],
    queryFn: () => followUpApi.getCallBatches(campaignId!, params).then(r => r.data),
    enabled: !!campaignId,
    placeholderData: keepPreviousData,
  })
}

export function useCallBatch(campaignId: string | undefined, batchId: string | undefined) {
  return useQuery({
    queryKey: ['followUp', campaignId, 'callBatches', batchId],
    queryFn: () => followUpApi.getCallBatch(campaignId!, batchId!).then(r => r.data),
    enabled: !!campaignId && !!batchId,
  })
}

// Batch writes move item queue state (alreadyQueued) and the Follow-up tab counts: refresh both.
function useInvalidateCallState(campaignId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'callBatches'] })
    queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'items'] })
  }
}

export function useCreateCallBatch(campaignId: string) {
  const invalidate = useInvalidateCallState(campaignId)
  return useMutation({
    mutationFn: (data: CreateCallBatchRequest) => followUpApi.createCallBatch(campaignId, data).then(r => r.data),
    onSuccess: invalidate,
  })
}

export function useAssignCallBatch(campaignId: string) {
  const invalidate = useInvalidateCallState(campaignId)
  return useMutation({
    mutationFn: ({ batchId, ...data }: AssignCallBatchRequest & { batchId: string }) =>
      followUpApi.assignCallBatch(campaignId, batchId, data),
    onSuccess: invalidate,
  })
}

export function useUnassignCallBatch(campaignId: string) {
  const invalidate = useInvalidateCallState(campaignId)
  return useMutation({
    mutationFn: (batchId: string) => followUpApi.unassignCallBatch(campaignId, batchId),
    onSuccess: invalidate,
  })
}

export function useDeleteCallBatch(campaignId: string) {
  const invalidate = useInvalidateCallState(campaignId)
  return useMutation({
    mutationFn: (batchId: string) => followUpApi.deleteCallBatch(campaignId, batchId),
    onSuccess: invalidate,
  })
}

export function useFollowUpScripts(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['followUp', campaignId, 'scripts'],
    queryFn: () => followUpApi.getScripts(campaignId!).then(r => r.data),
    enabled: !!campaignId,
  })
}

export function useSaveFollowUpScript(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveFollowUpScriptRequest) => followUpApi.saveScript(campaignId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'scripts'] })
    },
  })
}
